import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { getProfile, updateProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import {
  ASK_DYNAMO_THOUGHT_FREQUENCIES,
  type AskDynamoThoughtFrequency,
} from "~/data/askDynamoThoughtFrequencies"

type EmailFrequency = "weekly" | "monthly"

export function NotificationPreferencesPage() {
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [emailEnabled, setEmailEnabled] = useState(true)
  const [emailFrequency, setEmailFrequency] = useState<EmailFrequency>("weekly")
  const [thoughtsEnabled, setThoughtsEnabled] = useState(true)
  const [thoughtFrequency, setThoughtFrequency] = useState<AskDynamoThoughtFrequency>("daily")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    getProfile()
      .then((data) => {
        setProfile(data)
        setEmailEnabled(data.kibble_enabled)
        setEmailFrequency(data.kibble_frequency === "monthly" ? "monthly" : "weekly")
        const thoughtPref = data.ask_dynamo_thought_preference
        if (thoughtPref) {
          setThoughtsEnabled(thoughtPref.enabled)
          setThoughtFrequency(thoughtPref.frequency === "off" ? "daily" : thoughtPref.frequency)
        }
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  function clearSuccessMessage() {
    setMessage(null)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await updateProfile({
        kibble_frequency: emailEnabled ? emailFrequency : "none",
        ask_dynamo_thought_frequency: thoughtsEnabled ? thoughtFrequency : "off",
      })
      setProfile(updated)
      setEmailEnabled(updated.kibble_enabled)
      setEmailFrequency(updated.kibble_frequency === "monthly" ? "monthly" : "weekly")
      const thoughtPref = updated.ask_dynamo_thought_preference
      if (thoughtPref) {
        setThoughtsEnabled(thoughtPref.enabled)
        setThoughtFrequency(thoughtPref.frequency === "off" ? "daily" : thoughtPref.frequency)
      }
      setMessage("Notification preferences saved.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save preferences")
    } finally {
      setSaving(false)
    }
  }

  if (loading || !profile) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="w-full p-2">
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            Notification Preferences
          </h1>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            Choose how Doggy Dynamo keeps in touch with you.
          </p>

          <section className="mt-6 space-y-3">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Email Updates</h2>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Receive tips, insights and updates about your dog by email.
            </p>
            <label className="flex items-center justify-between gap-3 text-sm text-[var(--color-text-primary)]">
              <span>Email updates</span>
              <input
                type="checkbox"
                checked={emailEnabled}
                onChange={(e) => {
                  clearSuccessMessage()
                  setEmailEnabled(e.target.checked)
                }}
                aria-label="Enable email updates"
              />
            </label>
            {emailEnabled && (
              <div className="flex gap-2">
                <button
                  type="button"
                  aria-pressed={emailFrequency === "weekly"}
                  onClick={() => {
                    clearSuccessMessage()
                    setEmailFrequency("weekly")
                  }}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    emailFrequency === "weekly"
                      ? "bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                      : "border border-[var(--color-divider)]"
                  }`}
                >
                  Weekly (every 7 days)
                </button>
                <button
                  type="button"
                  aria-pressed={emailFrequency === "monthly"}
                  onClick={() => {
                    clearSuccessMessage()
                    setEmailFrequency("monthly")
                  }}
                  className={`rounded-lg px-3 py-2 text-sm ${
                    emailFrequency === "monthly"
                      ? "bg-[var(--color-text-primary)] text-[var(--color-surface-card)]"
                      : "border border-[var(--color-divider)]"
                  }`}
                >
                  Monthly (every 28 days)
                </button>
              </div>
            )}
          </section>

          <section className="mt-8 space-y-3">
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">
              Ask Dynamo Thoughts
            </h2>
            <p className="text-sm text-[var(--color-text-secondary)]">
              Receive inspirational Ask Dynamo thoughts to spark ideas and conversations.
            </p>
            <label className="flex items-center justify-between gap-3 text-sm text-[var(--color-text-primary)]">
              <span>Ask Dynamo Thoughts</span>
              <input
                type="checkbox"
                checked={thoughtsEnabled}
                onChange={(e) => {
                  clearSuccessMessage()
                  setThoughtsEnabled(e.target.checked)
                }}
                aria-label="Enable Ask Dynamo Thoughts"
              />
            </label>
            {thoughtsEnabled && (
              <div className="flex flex-wrap gap-2">
                {ASK_DYNAMO_THOUGHT_FREQUENCIES.filter((item) => item.value !== "off").map((item) => (
                  <button
                    key={item.value}
                    type="button"
                    aria-pressed={thoughtFrequency === item.value}
                    onClick={() => {
                      clearSuccessMessage()
                      setThoughtFrequency(item.value)
                    }}
                    className={`rounded-lg px-3 py-2 text-sm ${
                      thoughtFrequency === item.value
                        ? "bg-[var(--color-ask-dynamo)] text-white"
                        : "border border-[var(--color-divider)]"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </section>

          {error && (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="mt-4 text-sm text-green-700" role="status">
              {message}
            </p>
          )}

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="mt-6 w-full rounded-lg bg-[var(--color-text-primary)] px-4 py-2.5 text-sm font-medium text-[var(--color-surface-card)] transition hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save notification preferences"}
          </button>

          <Link
            to="/profile"
            className="mt-6 inline-block text-sm font-medium text-[var(--color-text-primary)] underline"
          >
            Back to Profile
          </Link>
        </div>
      </LoggedInScreenCard>
    </PageShell>
  )
}
